import type { ComputedRef } from "vue";
import type { Maybe } from "graphql/jsutils/Maybe";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import type {
  XrefIntakes,
  XrefPumps,
  XrefSeals
} from "@/composables/forms/formTypes";
import { computed } from "vue";
import { intakeHasGasSeparator } from "@/libs/calculations/intakes";
import type { IntakeInfo } from "../fluidPropertiesPipeline";
import type { EquipmentOrderingCtx } from "./context";

// Ordered and primary/bottom equipment lists.
export const defineEquipmentOrdering = (ctx: EquipmentOrderingCtx) => {
  const {
    _scenario,
    _equipment,
    _parameters,
    _pumps,
    _seals,
    _intakes,
    _housings,
    _cables,
    _yToolBypassTubings,
    _yToolEquipmentTubings
  } = ctx;

  const pumpHz = computed(() => _parameters.value?.pump_hz || 60);

  const isScenarioEspShore = computed(
    () => _scenario.value?.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );

  // Get y-tool properties from equipment based on stack
  // const yToolData = computed(() => getYToolProperties(_equipment.value, stack));

  // For the ordered y tool tubings, we should return empty if we're not using a y tool
  const orderedYToolBypassTubings = computed(() => {
    const bypassTubingsOrder: string[] =
      _equipment.value?.uphole_y_tool_bypass_tubing_order || [];
    return bypassTubingsOrder
      .map(id => _yToolBypassTubings.value[id])
      .filter(
        xrefBypassTubing =>
          xrefBypassTubing &&
          xrefBypassTubing.tubing_id &&
          !xrefBypassTubing.journal_deleted_at
      );
  });

  const orderedYToolEquipmentTubings = computed(() => {
    const equipmentTubingsOrder: string[] =
      _equipment.value?.uphole_y_tool_equipment_tubing_order || [];
    return equipmentTubingsOrder
      .map(id => _yToolEquipmentTubings.value[id])
      .filter(
        xrefEquipmentTubing =>
          xrefEquipmentTubing &&
          xrefEquipmentTubing.tubing_id &&
          !xrefEquipmentTubing.journal_deleted_at
      );
  });

  // These are the ordered sets of equipment that have been
  // selected in the form by the user.
  const orderedIntakes = computed(() => {
    const intakesOrder: string[] = _equipment.value?.intakes_order || [];
    return intakesOrder
      .map(id => _intakes.value[id])
      .filter(
        xrefIntake =>
          xrefIntake && xrefIntake.intake_id && !xrefIntake.journal_deleted_at
      );
  });

  // bolt_on on intake
  const intakesContainNonBoltOn = computed(() => {
    return orderedIntakes.value.some(
      intake => intake.r_intake?.bolt_on === false
    );
  });

  const orderedIntakesInfo: ComputedRef<IntakeInfo[]> = computed(() => {
    return orderedIntakes.value.map(i => {
      const r_intake = i.r_intake;
      return {
        maxRateBpd: r_intake?.max_rate_bpd || 0,
        eff1: r_intake?.eff1 || 0,
        eff2: r_intake?.eff2 || 0
      };
    });
  });

  // Determines if there is a gas separator intake selected
  const intakesContainGasSeparator = computed(() => {
    return orderedIntakes.value.some(i =>
      intakeHasGasSeparator(i.r_intake?.eff1, i.r_intake?.eff2)
    );
  });

  const descendingOrderPumps = computed(() => {
    const pumpsOrder: string[] = _equipment.value?.pumps_order || [];
    return pumpsOrder
      .map(id => _pumps.value[id])
      .filter(
        xrefPump =>
          xrefPump &&
          xrefPump.pump_id &&
          xrefPump.r_pump &&
          !xrefPump.journal_deleted_at
      );
  });

  const ascendingOrderPumps = computed(() =>
    [...descendingOrderPumps.value].reverse()
  );

  const orderedHousings = computed(() => {
    const housingsOrder: string[] = descendingOrderPumps.value.flatMap(
      xrefPump => _pumps.value[xrefPump.id].housings_order || []
    );

    return housingsOrder
      .map(id => _housings.value[id])
      .filter(
        xrefHousing =>
          xrefHousing &&
          xrefHousing.housing_id &&
          xrefHousing.r_housing &&
          !xrefHousing.journal_deleted_at
      );
  });

  const descendingPumpsWithHousingIndex = computed(() => {
    let index = 0;
    return descendingOrderPumps.value.flatMap(p => {
      if (!p.r_pump?.r_housings?.length || !orderedHousings.value.length) {
        return {
          pump: p,
          housingIndex: index++
        };
      }

      const housings =
        p.housings_order?.flatMap((hId: string) => {
          const orderedIndex = orderedHousings.value.findIndex(
            h => h.id === hId
          );
          if (orderedIndex < 0) return [];
          return {
            pump: p,
            housing: orderedHousings.value[orderedIndex],
            housingIndex: index++
          };
        }) || [];

      if (!housings.length) {
        // no housings incremented the index at all so do it here for next pump.
        index++;
      }
      return housings;
    });
  });

  const descendingOrderSeals = computed(() => {
    const sealsOrder: string[] = _equipment.value?.seals_order || [];
    return sealsOrder
      .map(id => _seals.value[id])
      .filter(
        xrefSeal => xrefSeal && xrefSeal.seal_id && !xrefSeal.journal_deleted_at
      );
  });

  const cablesOrder = computed(() => {
    return _equipment.value?.cables_order || [];
  });

  // This is because of the surface cable
  const currentCables = computed(() =>
    cablesOrder.value
      .map(id => _cables.value[id])
      .filter(
        xrefCable =>
          xrefCable && xrefCable.cable_id && !xrefCable.journal_deleted_at
      )
  );

  const primaryPump = computed<Maybe<XrefPumps[number]>>(() =>
    descendingOrderPumps.value.length ? descendingOrderPumps.value[0] : null
  );

  const bottomPump = computed<Maybe<XrefPumps[number]>>(() =>
    ascendingOrderPumps.value.length ? ascendingOrderPumps.value[0] : null
  );

  const primarySeal = computed<Maybe<XrefSeals[number]>>(() =>
    descendingOrderSeals.value.length ? descendingOrderSeals.value[0] : null
  );

  const bottomSeal = computed(() =>
    descendingOrderSeals.value.length > 1
      ? descendingOrderSeals.value[descendingOrderSeals.value.length - 1]
      : null
  );

  const primaryIntake = computed<Maybe<XrefIntakes[number]>>(() =>
    orderedIntakes.value.length ? orderedIntakes.value[0] : null
  );

  const bottomIntake = computed<Maybe<XrefIntakes[number]>>(() =>
    orderedIntakes.value.length
      ? orderedIntakes.value[orderedIntakes.value.length - 1]
      : null
  );

  return {
    pumpHz,
    isScenarioEspShore,
    orderedYToolBypassTubings,
    orderedYToolEquipmentTubings,
    orderedIntakes,
    intakesContainNonBoltOn,
    orderedIntakesInfo,
    intakesContainGasSeparator,
    descendingOrderPumps,
    ascendingOrderPumps,
    orderedHousings,
    descendingPumpsWithHousingIndex,
    descendingOrderSeals,
    cablesOrder,
    currentCables,
    primaryPump,
    bottomPump,
    primarySeal,
    bottomSeal,
    primaryIntake,
    bottomIntake
  };
};

export type EquipmentOrderingCalcs = ReturnType<typeof defineEquipmentOrdering>;
